import { useEffect, useState } from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import { Text, Icon, theme, styles as baseStyles } from '@socrait/react';

import { Period, Student } from '~/services/types';
import { MainLayout } from '~/layouts/MainLayout';
import {
  useStudentGenerateMessageMutation,
  useStudentDataLogQuery,
  type IncludeKey,
  type Timeframe,
  type MessageFormat,
} from '~/services/studentData';

import { GenerateMessagePanel } from './GenerateMessagePanel';
import { MessagePanel } from './MessagePanel';
import { DataLogPanel } from './DataLogPanel';

export type IncludeOptions = { praises: boolean; warnings: boolean; attendance: boolean };

const DEFAULT_INCLUDE: IncludeOptions = { praises: true, warnings: true, attendance: true };
const DEFAULT_TIMEFRAME: Timeframe = 'week';
const DEFAULT_FORMAT: MessageFormat = 'summary';

type StudentDataScreenProps = {
  activePeriod: Period | undefined;
  student: Student;
  onClose: () => void;
  onAfterPeriodSelect?: () => void;
};

export function StudentDataScreen({ activePeriod, student, onClose, onAfterPeriodSelect }: StudentDataScreenProps) {
  // Pending state — what the user is currently editing
  const [pendingInclude, setPendingInclude] = useState<IncludeOptions>(DEFAULT_INCLUDE);
  const [pendingTimeframe, setPendingTimeframe] = useState<Timeframe>(DEFAULT_TIMEFRAME);
  const [pendingFormat, setPendingFormat] = useState<MessageFormat>(DEFAULT_FORMAT);

  // Committed state — snapshot of the last "Go!" press (drives panels)
  const [committedInclude, setCommittedInclude] = useState<IncludeOptions>(DEFAULT_INCLUDE);
  const [committedTimeframe, setCommittedTimeframe] = useState<Timeframe>(DEFAULT_TIMEFRAME);
  const [committedFormat, setCommittedFormat] = useState<MessageFormat>(DEFAULT_FORMAT);

  const { mutate: generateMessage, message, status: messageStatus } = useStudentGenerateMessageMutation();
  const committedIncludeArr = (Object.keys(committedInclude) as IncludeKey[]).filter((k) => committedInclude[k]);

  const { data: dataLogData, status: dataLogStatus } = useStudentDataLogQuery({
    studentId: student.id,
    periodId: activePeriod?.id ?? '',
    periodCreatedAt: activePeriod?.createdAt ?? '',
    timeframe: committedTimeframe,
    include: committedIncludeArr,
  });

  // Auto-load on mount with default committed state
  useEffect(() => {
    const includeArr = (Object.keys(DEFAULT_INCLUDE) as IncludeKey[]).filter((k) => DEFAULT_INCLUDE[k]);
    generateMessage({
      studentId: student.id,
      periodId: activePeriod?.id ?? '',
      periodCreatedAt: activePeriod?.createdAt ?? '',
      timeframe: DEFAULT_TIMEFRAME,
      include: includeArr,
      format: DEFAULT_FORMAT,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDirty =
    JSON.stringify(pendingInclude) !== JSON.stringify(committedInclude) ||
    pendingTimeframe !== committedTimeframe ||
    pendingFormat !== committedFormat;

  const hasAnyInclude = Object.values(pendingInclude).some(Boolean);

  const handleIncludeChange = (key: IncludeKey, checked: boolean) => {
    setPendingInclude((prev) => ({ ...prev, [key]: checked }));
  };

  const handleGenerate = () => {
    const newInclude = pendingInclude;
    const newTimeframe = pendingTimeframe;
    const newFormat = pendingFormat;

    setCommittedInclude(newInclude);
    setCommittedTimeframe(newTimeframe);
    setCommittedFormat(newFormat);

    const includeArr = (Object.keys(newInclude) as IncludeKey[]).filter((k) => newInclude[k]);
    generateMessage({
      studentId: student.id,
      periodId: activePeriod?.id ?? '',
      periodCreatedAt: activePeriod?.createdAt ?? '',
      timeframe: newTimeframe,
      include: includeArr,
      format: newFormat,
    });
  };

  return (
    <MainLayout onAfterPeriodSelect={onAfterPeriodSelect}>
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerText}>
            {activePeriod?.name} - <Text style={[baseStyles.boldText, styles.headerText]}>{student.voiceName}</Text>
          </Text>
          <Pressable onPress={onClose} style={styles.closeButton}>
            <Icon type="close" />
          </Pressable>
        </View>
        <View style={styles.content}>
          <GenerateMessagePanel
            includeOptions={pendingInclude}
            timeframe={pendingTimeframe}
            format={pendingFormat}
            isDirty={isDirty}
            hasAnyInclude={hasAnyInclude}
            onIncludeChange={handleIncludeChange}
            onTimeframeChange={setPendingTimeframe}
            onFormatChange={setPendingFormat}
            onGenerate={handleGenerate}
          />
          <MessagePanel
            included={committedInclude}
            timeframe={committedTimeframe}
            format={committedFormat}
            message={message}
            isLoading={messageStatus === 'pending'}
            isError={messageStatus === 'error'}
          />
          <DataLogPanel
            events={dataLogData?.events}
            counts={dataLogData?.counts}
            isLoading={dataLogStatus === 'pending'}
            isError={dataLogStatus === 'error'}
          />
        </View>
      </View>
    </MainLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
  header: {
    alignItems: 'center',
    borderBottomColor: theme.colors.gray,
    borderBottomWidth: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.xlarge * 2,
    paddingTop: theme.spacing.xxlarge,
    paddingBottom: theme.spacing.large,
  },
  headerText: {
    fontSize: theme.fontSizes.large,
  },
  closeButton: {
    padding: theme.spacing.medium,
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    gap: theme.spacing.xxlarge,
    padding: theme.spacing.xxlarge,
    overflow: 'hidden',
  },
});
